import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, CheckCircle2, CalendarClock } from "lucide-react";
import { submitContact } from "@/lib/api";

export default function BookDemo() {
  const [form, setForm] = useState({ name: "", college: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError("Please add your name and email."); return; }
    setBusy(true); setError("");
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        company: form.college.trim() || undefined,
        message: `Demo request from a placement cell.\nCollege: ${form.college.trim() || "—"}\nPhone: ${form.phone.trim() || "—"}`,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <section id="book-demo" className="section">
      <div className="container-x">
        <div className="rounded-[2.5rem] bg-[#F5F3ED] border border-stone-200 p-8 md:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="tag"><CalendarClock size={12} /> Book a demo</span>
            <h2 className="font-display text-4xl md:text-5xl mt-6 leading-[1.05]">
              See your batch&apos;s readiness — <span className="italic text-stone-500">live.</span>
            </h2>
            <p className="mt-5 text-lg text-stone-700 leading-relaxed max-w-lg">
              A 20-minute walkthrough for your placement cell. We&apos;ll show the readiness dashboard,
              campus drives, and reports on a sample batch — and scope a plan to your college.
            </p>
            <p className="mt-4 text-sm text-stone-500">
              Pricing is tailored to your batch size and needs — no per-seat surprises. We&apos;ll share it on the call.
            </p>
          </div>

          <div className="rounded-3xl bg-white border border-stone-200 p-6 md:p-7">
            {done ? (
              <div className="text-center py-8" data-testid="demo-done">
                <div className="inline-flex p-3 rounded-2xl bg-emerald-100 text-emerald-700 mb-3"><CheckCircle2 size={24} /></div>
                <h3 className="font-display text-2xl">Thanks — we&apos;ll be in touch.</h3>
                <p className="mt-2 text-stone-600 text-sm">Our team will email you within one working day to schedule your demo.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="demo-form">
                <div>
                  <label className="overline">Your name *</label>
                  <input value={form.name} onChange={set("name")} data-testid="demo-name" placeholder="Placement officer name"
                    className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
                </div>
                <div>
                  <label className="overline">College / institution</label>
                  <input value={form.college} onChange={set("college")} data-testid="demo-college" placeholder="e.g. Demo Institute of Technology"
                    className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="overline">Work email *</label>
                    <input type="email" value={form.email} onChange={set("email")} data-testid="demo-email" placeholder="you@college.edu"
                      className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                  <div>
                    <label className="overline">Phone</label>
                    <input type="tel" value={form.phone} onChange={set("phone")} data-testid="demo-phone" placeholder="+91 …"
                      className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                </div>
                {error && <p className="text-sm text-red-700">{error}</p>}
                <button type="submit" disabled={busy} data-testid="demo-submit"
                  className="btn-primary w-full justify-center mt-1 disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <>Request my demo <ArrowUpRight size={16} /></>}
                </button>
                <p className="text-xs text-stone-400 text-center">No spam. We only use this to schedule your walkthrough.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
